import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { userAPI } from '../utils/api';
import './CertificatePage.scss';

type Certificate = {
  title: string;
  titleRu: string;
  holderName: string;
  email: string;
  planType: string;
  issuedAt: string;
  brand: string;
};

const CertificatePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const certRef = useRef<HTMLDivElement>(null);
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login?redirect=/certificate', { replace: true });
      return;
    }
    userAPI
      .getCertificate()
      .then(({ data }) => setCertificate(data.certificate))
      .catch((err) => {
        setError(err?.response?.data?.error || t('certificate.errors.loadFailed'));
      })
      .finally(() => setLoading(false));
  }, [navigate, t]);

  const issuedLabel = certificate?.issuedAt
    ? new Date(certificate.issuedAt).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'es' ? 'es-ES' : 'en-GB')
    : '';

  const handleDownload = async () => {
    if (!certRef.current || !certificate) return;
    setSaving(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        backgroundColor: '#0a0a12',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      link.download = `numerolife-new-start-2026-${certificate.holderName.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error(e);
      alert(t('certificate.errors.downloadFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="certificate-page">
        <p>{t('certificate.loading')}</p>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="certificate-page">
        <button type="button" className="page-back" onClick={() => navigate('/dashboard')}>
          ← {t('nav.back')}
        </button>
        <p className="certificate-page__error" role="alert">{error || t('certificate.errors.loadFailed')}</p>
      </div>
    );
  }

  const title = i18n.language === 'ru' ? certificate.titleRu : certificate.title;

  return (
    <div className="certificate-page">
      <button type="button" className="page-back" onClick={() => navigate('/dashboard')}>
        ← {t('nav.back')}
      </button>
      <h1 className="certificate-page__heading">{t('certificate.pageTitle')}</h1>
      <p className="certificate-page__hint">{t('certificate.hint')}</p>

      <div className="certificate-page__sheet" ref={certRef}>
        <div className="certificate-page__brand">{certificate.brand}</div>
        <p className="certificate-page__label">{t('certificate.presents')}</p>
        <h2 className="certificate-page__title">{title}</h2>
        <p className="certificate-page__to">{t('certificate.awardedTo')}</p>
        <p className="certificate-page__name">{certificate.holderName}</p>
        <p className="certificate-page__meta">
          {t('certificate.issuedOn')}: {issuedLabel}
        </p>
        <p className="certificate-page__footer">{t('certificate.footer')}</p>
      </div>

      <div className="certificate-page__actions">
        <button type="button" className="btn-primary" onClick={handleDownload} disabled={saving}>
          {saving ? t('certificate.saving') : t('certificate.download')}
        </button>
        <button type="button" className="btn-secondary" onClick={() => window.print()}>
          {t('certificate.print')}
        </button>
      </div>
    </div>
  );
};

export default CertificatePage;
